import React, { useState } from 'react';
import { Edit2, FileText, Loader2, Trash2 } from 'lucide-react';
import { DeleteConfirmModal } from '../DeleteConfirmModal';
import type { VaginalDelivery } from '../../types/clinical';

export interface VaginalDeliveryCardProps {
  delivery: VaginalDelivery;
  isDoctor: boolean;
  editingId: string | null;
  editingData: Partial<VaginalDelivery>;
  editSaving: boolean;
  setEditingId: (id: string | null) => void;
  setEditingData: React.Dispatch<React.SetStateAction<Partial<VaginalDelivery>>>;
  onSaveEdit: () => void;
  onDelete: (deliveryId: string) => Promise<void>;
  onGeneratePdf: (deliveryId: string) => Promise<void>;
  pdfLoadingId: string | null;
}

export function VaginalDeliveryCard({
  delivery: d,
  isDoctor,
  editingId,
  editingData,
  editSaving,
  setEditingId,
  setEditingData,
  onSaveEdit,
  onDelete,
  onGeneratePdf,
  pdfLoadingId,
}: VaginalDeliveryCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isEditing = editingId === d.id;

  const rhDisplay = d.rhFactor === 'positive' ? '+' : d.rhFactor === 'negative' ? '−' : '';
  const bloodDisplay = d.bloodGroup ? `${d.bloodGroup}${rhDisplay}` : '';
  const utero = d.uterotonic === 'Other' ? d.uterotonicOther : d.uterotonic;

  const DELIVERY_MODE_LABELS: Record<string, string> = {
    NVD: 'Normal Vaginal Delivery (NVD)',
    NVS: 'Normal Vaginal — Stillbirth',
    Vacuum: 'Vacuum (Ventouse)',
    Forceps: 'Forceps',
    Breech: 'Vaginal Breech Delivery',
    Other: 'Other',
  };

  return (
    <>
      {showDeleteConfirm && (
        <DeleteConfirmModal
          title="Delete Delivery"
          description={`This will permanently delete the delivery record (${d.date}). This action cannot be undone.`}
          onConfirm={async () => { await onDelete(d.id); setShowDeleteConfirm(false); }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
        {isEditing ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input
                  type="date"
                  value={editingData.date || ''}
                  onChange={e => setEditingData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Hospital</label>
                <input
                  value={editingData.hospital || ''}
                  onChange={e => setEditingData(prev => ({ ...prev, hospital: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Delivery Mode</label>
                <select
                  value={editingData.deliveryMode || ''}
                  onChange={e => setEditingData(prev => ({ ...prev, deliveryMode: e.target.value as VaginalDelivery['deliveryMode'] }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
                >
                  <option value="">— Select —</option>
                  <option value="NVD">Normal Vaginal Delivery (NVD)</option>
                  <option value="Vacuum">Vacuum (Ventouse)</option>
                  <option value="Forceps">Forceps</option>
                  <option value="Breech">Vaginal Breech Delivery</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Notes</label>
              <textarea
                value={editingData.notes || ''}
                onChange={e => setEditingData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Post Partum</label>
              <textarea
                value={editingData.postPartum || ''}
                onChange={e => setEditingData(prev => ({ ...prev, postPartum: e.target.value }))}
                rows={3}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Baby Weight (g)</label>
                <input type="number" step={10} min={0} max={6000} value={editingData.babyWeight ?? ''} onChange={e => setEditingData(prev => ({ ...prev, babyWeight: e.target.value ? parseInt(e.target.value) : null }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Sex</label>
                <select value={editingData.babySex || ''} onChange={e => setEditingData(prev => ({ ...prev, babySex: e.target.value as VaginalDelivery['babySex'] }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
                  <option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="unknown">Unknown</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Apgar 1m</label>
                <input type="number" min={0} max={10} value={editingData.apgar1min ?? ''} onChange={e => setEditingData(prev => ({ ...prev, apgar1min: e.target.value ? parseInt(e.target.value) : null }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Apgar 5m</label>
                <input type="number" min={0} max={10} value={editingData.apgar5min ?? ''} onChange={e => setEditingData(prev => ({ ...prev, apgar5min: e.target.value ? parseInt(e.target.value) : null }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1 text-xs text-gray-700"><input type="checkbox" checked={editingData.bcgGiven || false} onChange={e => setEditingData(prev => ({ ...prev, bcgGiven: e.target.checked }))} className="h-3.5 w-3.5 rounded" /> BCG</label>
              <label className="flex items-center gap-1 text-xs text-gray-700"><input type="checkbox" checked={editingData.polioGiven || false} onChange={e => setEditingData(prev => ({ ...prev, polioGiven: e.target.checked }))} className="h-3.5 w-3.5 rounded" /> Polio</label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onSaveEdit}
                disabled={editSaving}
                className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={() => setEditingId(null)} className="px-3 py-1 text-xs text-gray-600">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900">
                Delivery — {DELIVERY_MODE_LABELS[d.deliveryMode] || d.deliveryMode || 'Mode not set'}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{d.date}{d.time && ` · ${d.time}`}</span>
                <button
                  onClick={() => onGeneratePdf(d.id)}
                  disabled={pdfLoadingId === d.id}
                  className="p-1 text-gray-400 hover:text-teal-600 disabled:opacity-50"
                  title="Generate PDF"
                >
                  {pdfLoadingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                </button>
                {isDoctor && (
                  <>
                    <button
                      onClick={() => {
                        setEditingId(d.id);
                        setEditingData({
                          date: d.date,
                          hospital: d.hospital,
                          deliveryMode: d.deliveryMode,
                          notes: d.notes,
                          postPartum: d.postPartum,
                        });
                      }}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Edit"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
            {d.hospital && <p className="text-xs text-gray-500 mt-1">{d.hospital}</p>}
            <div className="flex flex-wrap gap-2 mt-1">
              {bloodDisplay && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-red-50 text-red-700 border border-red-200">
                  Blood: {bloodDisplay}
                </span>
              )}
              {d.caesarean && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                  Caesarean
                </span>
              )}
              {utero && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                  {utero}
                </span>
              )}
            </div>
            {(d.babyWeight || d.babySex || d.apgar1min != null || d.apgar5min != null || d.bcgGiven || d.polioGiven) && (
              <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-600">
                {d.babyWeight && <span>{d.babyWeight >= 1000 ? `${(d.babyWeight / 1000).toFixed(1)}kg` : `${d.babyWeight}g`}</span>}
                {d.babySex && <span className="capitalize">{d.babySex}</span>}
                {(d.apgar1min != null || d.apgar5min != null) && <span>Apgar {d.apgar1min ?? '?'}/{d.apgar5min ?? '?'}</span>}
                {d.bcgGiven && <span className="text-green-700">BCG &#10003;</span>}
                {d.polioGiven && <span className="text-green-700">Polio &#10003;</span>}
              </div>
            )}
            {d.notes && <p className="text-sm text-gray-600 mt-1">{d.notes}</p>}
            {d.updatedBy && <p className="text-xs text-gray-400 mt-2">Edited {new Date(d.updatedAt).toLocaleDateString()}</p>}
          </>
        )}
      </div>
    </>
  );
}
